import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Image, Video, Music, X, Type, Plus, Upload } from "lucide-react";
import { validateFileSize, validateVideo } from "@/utils/mediaUtils";
import { useQuery } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
interface StoryUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
interface AudioLibraryItem {
  id: string;
  title: string;
  artist: string;
  audio_url: string;
  cover_url?: string;
}
export const StoryUploadDialog = ({
  open,
  onOpenChange
}: StoryUploadDialogProps) => {
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [audioCoverFile, setAudioCoverFile] = useState<File | null>(null);
  const [audioCoverPreview, setAudioCoverPreview] = useState<string | null>(null);
  const [audioTitle, setAudioTitle] = useState("");
  const [audioArtist, setAudioArtist] = useState("");
  const [storyText, setStoryText] = useState("");
  const [textPosition, setTextPosition] = useState({
    x: 0.5,
    y: 0.5
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [audioStartTime, setAudioStartTime] = useState(0);
  const [showAudioLibrary, setShowAudioLibrary] = useState(false);
  const [audioDuration, setAudioDuration] = useState(15);
  const audioRef = useRef<HTMLAudioElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const {
    data: audioLibrary
  } = useQuery({
    queryKey: ['audioLibrary'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('audio_library').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      return data as AudioLibraryItem[];
    }
  });
  const {
    data: profile
  } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const {
        data,
        error
      } = await supabase.from('profiles').select('avatar_url').eq('id', user.id).single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (!validateFileSize(file)) {
        toast({
          title: "Error",
          description: "File size must be 10MB or less",
          variant: "destructive"
        });
        return;
      }
      if (file.type.startsWith("video/")) {
        await validateVideo(file);
      }
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const handleAudioSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (!file.type.startsWith("audio/")) {
        throw new Error("Please select an audio file");
      }
      if (!validateFileSize(file)) {
        throw new Error("Audio file must be 10MB or less");
      }
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const handleCoverPhotoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (!file.type.startsWith("image/")) {
        throw new Error("Please select an image file");
      }
      if (!validateFileSize(file)) {
        throw new Error("Image must be 10MB or less");
      }
      setAudioCoverFile(file);
      setAudioCoverPreview(URL.createObjectURL(file));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const handleTextDrag = (e: React.MouseEvent) => {
    if (!isDragging || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTextPosition({
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y))
    });
  };
  const selectLibraryAudio = (item: AudioLibraryItem) => {
    setAudioPreview(item.audio_url);
    setAudioTitle(item.title);
    setAudioArtist(item.artist);
    if (item.cover_url) {
      setAudioCoverPreview(item.cover_url);
    }
    setShowAudioLibrary(false);
  };
  const handleUpload = async () => {
    if (!mediaFile || !user) return;
    setIsUploading(true);
    try {
      // Upload media file
      const mediaExt = mediaFile.name.split(".").pop();
      const mediaPath = `${crypto.randomUUID()}.${mediaExt}`;
      const {
        error: uploadError
      } = await supabase.storage.from("post-media").upload(mediaPath, mediaFile);
      if (uploadError) throw uploadError;
      const {
        data: mediaData
      } = supabase.storage.from("post-media").getPublicUrl(mediaPath);

      // Upload audio cover if exists
      let coverUrl = audioCoverPreview || profile?.avatar_url;
      if (audioCoverFile) {
        const coverExt = audioCoverFile.name.split(".").pop();
        const coverPath = `${crypto.randomUUID()}.${coverExt}`;
        const {
          error: coverError
        } = await supabase.storage.from("post-media").upload(coverPath, audioCoverFile);
        if (coverError) throw coverError;
        const {
          data: coverData
        } = supabase.storage.from("post-media").getPublicUrl(coverPath);
        coverUrl = coverData.publicUrl;
      }

      // Upload audio if exists and not from library
      let audioUrl = audioPreview;
      if (audioFile) {
        const audioExt = audioFile.name.split(".").pop();
        const audioPath = `${crypto.randomUUID()}.${audioExt}`;
        const {
          error: audioError
        } = await supabase.storage.from("post-media").upload(audioPath, audioFile);
        if (audioError) throw audioError;
        const {
          data: audioData
        } = supabase.storage.from("post-media").getPublicUrl(audioPath);
        audioUrl = audioData.publicUrl;

        // Add to audio library
        if (audioTitle && audioArtist) {
          const {
            error: libraryError
          } = await supabase.from("audio_library").insert([{
            title: audioTitle,
            artist: audioArtist,
            audio_url: audioUrl,
            cover_url: coverUrl,
            uploaded_by: user.id,
            duration: audioDuration
          }]);
          if (libraryError) throw libraryError;
        }
      }

      // Create story that expires in 24 hours
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);
      const {
        error: storyError
      } = await supabase.from("stories").insert([{
        user_id: user.id,
        media_url: mediaData.publicUrl,
        media_type: mediaFile.type,
        expires_at: expiresAt.toISOString(),
        text_overlay: storyText || null,
        text_position: textPosition,
        audio_url: audioUrl,
        audio_start_time: audioUrl ? audioStartTime : null,
        audio_title: audioTitle || null,
        audio_artist: audioArtist || null
      }]);
      if (storyError) throw storyError;
      toast({
        title: "Success",
        description: "Your story has been uploaded!"
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleAudioLoad = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget;
    setAudioDuration(Math.min(audio.duration, 15)); // Limit to 15 seconds max
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Story</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[80vh]">
          <div className="space-y-4 p-4">
            {!mediaPreview ? <div className="grid grid-cols-2 gap-4">
                <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted">
                  <Image className="w-8 h-8 mb-2" />
                  <span className="text-sm">Upload Image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                </label>

                <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted">
                  <Video className="w-8 h-8 mb-2" />
                  <span className="text-sm">Upload Video</span>
                  <input type="file" accept="video/*" className="hidden" onChange={handleFileSelect} />
                </label>
              </div> : <div ref={previewRef} className="relative flex-1 min-h-[50vh]" onMouseMove={handleTextDrag} onMouseDown={() => storyText && setIsDragging(true)} onMouseUp={() => setIsDragging(false)} onMouseLeave={() => setIsDragging(false)}>
                {mediaFile?.type.startsWith("image/") ? <img src={mediaPreview} alt="Preview" className="w-full h-full object-contain rounded-lg" /> : <video src={mediaPreview} controls className="w-full h-full rounded-lg" />}
                <button onClick={() => {
              setMediaFile(null);
              setMediaPreview(null);
            }} className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70">
                  <X className="w-4 h-4 text-white" />
                </button>
                {storyText && <div className="absolute p-2 bg-black/50 text-white rounded cursor-move" style={{
              left: `${textPosition.x * 100}%`,
              top: `${textPosition.y * 100}%`,
              transform: 'translate(-50%, -50%)'
            }}>
                    {storyText}
                  </div>}
              </div>}

            {mediaFile && <div className="space-y-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    <span>Add Text</span>
                  </label>
                  <Textarea value={storyText} onChange={e => setStoryText(e.target.value)} placeholder="Add text to your story..." className="resize-none" />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Music className="w-4 h-4" />
                      <span>Add Music</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowAudioLibrary(prev => !prev)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </label>

                  {showAudioLibrary ? <div className="space-y-2 p-2 border rounded-lg">
                      <h4 className="font-medium">Audio Library</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {audioLibrary?.map(item => <button key={item.id} onClick={() => selectLibraryAudio(item)} className="w-full text-left p-2 hover:bg-muted rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={item.cover_url} />
                                <AvatarFallback>
                                  <Music className="w-4 h-4" />
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{item.title}</div>
                                <div className="text-sm text-muted-foreground">{item.artist}</div>
                              </div>
                            </div>
                            <Plus className="w-4 h-4" />
                          </button>)}
                      </div>
                    </div> : <>
                      <Input type="file" accept="audio/*" onChange={handleAudioSelect} />
                      {(audioFile || audioPreview) && <>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Song title" value={audioTitle} onChange={e => setAudioTitle(e.target.value)} />
                            <Input placeholder="Artist name" value={audioArtist} onChange={e => setAudioArtist(e.target.value)} />
                          </div>

                          <div className="flex items-center gap-2">
                            <Avatar className="w-16 h-16">
                              <AvatarImage src={audioCoverPreview || profile?.avatar_url} />
                              <AvatarFallback>
                                <Music className="w-6 h-6" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted">
                                <Upload className="w-4 h-4 mb-1" />
                                <span className="text-sm">Upload Cover</span>
                                <input type="file" accept="image/*" className="hidden" onChange={handleCoverPhotoSelect} />
                              </label>
                            </div>
                          </div>

                          <audio ref={audioRef} src={audioPreview} controls className="w-full" onLoadedMetadata={handleAudioLoad} />
                          <div className="space-y-2">
                            <label>Select Start Time (max 15s)</label>
                            <Slider value={[audioStartTime]} onValueChange={value => setAudioStartTime(value[0])} max={audioDuration} step={0.1} className="[&_.bg-primary]:bg-flux-cyan [&_.hover:bg-primary]:hover:bg-flux-cyan/80" />
                            <div className="text-sm text-muted-foreground text-right">
                              {audioStartTime.toFixed(1)}s
                            </div>
                          </div>
                        </>}
                    </>}
                </div>

                <Button onClick={handleUpload} disabled={isUploading} className="w-full bg-flux-cyan hover:bg-flux-cyan/80 text-slate-50 bg-zinc-700 hover:bg-zinc-600">
                  {isUploading ? "Uploading..." : "Share Story"}
                </Button>
              </div>}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
};